/**
 * OAuthCallbackPage
 *
 * Handles OAuth provider callback and token exchange.
 * Shows animated loading overlay on success before navigating to dashboard.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  oauth2Service,
  OAUTH2_PROVIDER,
  validateState,
  oauth2Session,
} from "@/lib/oauth2";
import { useAuthStore } from "@/stores/authStore";
import { useTenantStore } from "@/stores/tenantStore";
import { mapTenantRole, mapTenantStatus } from "@/types/tenant";
import { tenantService } from "@/services";
import { LoadingOverlay } from "@/components/auth/LoadingOverlay";
import { toast } from "sonner";
import { jwtDecode } from "jwt-decode";

interface JwtPayload {
  exp?: number;
  iat?: number;
  sub?: string;
}

const getTokenExpiry = (token: string): number | null => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

const generateSlug = (email: string): string => {
  return email
    .split("@")[0]
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-");
};

export default function OAuthCallbackPage() {
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessedRef = useRef(false);
  const { t } = useTranslation("auth");

  const initializeTenantContext = async (
    userEmail: string,
    userFirstName: string,
  ) => {
    const tenants = await tenantService.getUserTenants();

    if (tenants.length === 0) {
      const defaultTenant = await tenantService.createTenant({
        name: `${userFirstName}'s Workspace`,
        slug: generateSlug(userEmail),
        type: "Individual",
      });

      useTenantStore.getState().setTenant({
        id: defaultTenant.id,
        name: defaultTenant.name,
        slug: defaultTenant.slug,
        role: mapTenantRole(defaultTenant.currentUserRole) || "Member",
        status: mapTenantStatus(defaultTenant.status),
        plan: defaultTenant.subscriptionPlanId,
        createdAt: defaultTenant.createdAt,
      });

      localStorage.setItem("lastTenantId", defaultTenant.id);
    } else {
      const lastTenantId = localStorage.getItem("lastTenantId");
      const selectedTenant =
        tenants.find((t) => t.id === lastTenantId) || tenants[0];

      const mappedTenants = tenants.map((t) => ({
        id: t.id,
        name: t.name,
        slug: t.slug,
        role: mapTenantRole(t.currentUserRole),
        status: mapTenantStatus(t.status),
        plan: t.subscriptionPlanId,
        createdAt: t.createdAt,
      }));

      useTenantStore.getState().loadTenants(mappedTenants);

      const mappedSelectedTenant =
        mappedTenants.find((t) => t.id === selectedTenant.id) ||
        mappedTenants[0];
      useTenantStore.getState().setTenant(mappedSelectedTenant);

      localStorage.setItem("lastTenantId", mappedSelectedTenant.id);
    }
  };

  const processCallback = useCallback(async () => {
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    try {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const errorParam = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      if (errorParam) {
        const message =
          errorDescription ||
          (errorParam === "access_denied"
            ? "OAuth authentication was cancelled"
            : "OAuth authentication failed");
        throw new Error(message);
      }

      if (!code)
        throw new Error("Missing authorization code from OAuth provider");
      if (!state)
        throw new Error("Missing state parameter from OAuth provider");

      const session = oauth2Session.get();
      if (!session) {
        throw new Error(
          "OAuth2 session not found. Please try logging in again.",
        );
      }

      if (!validateState(state, session.state)) {
        throw new Error(
          "Invalid state parameter - possible CSRF attack detected",
        );
      }

      if (oauth2Session.isExpired(session)) {
        throw new Error("OAuth2 session expired. Please try logging in again.");
      }

      // Token exchange
      const response = await oauth2Service.callback({
        provider: session.provider || OAUTH2_PROVIDER,
        code,
        state,
        codeVerifier: session.codeVerifier,
      });

      // Map to auth store
      const tokenExpiresAt = getTokenExpiry(response.accessToken);
      const user = {
        id: response.user.id,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        role: "user" as const,
        emailVerified: response.user.isActive,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      useAuthStore.setState({
        user,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        isAuthenticated: true,
        tokenExpiresAt,
      });

      const authData = {
        state: {
          user,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          isAuthenticated: true,
          tokenExpiresAt,
        },
      };
      localStorage.setItem("wicloud_auth", JSON.stringify(authData));

      oauth2Session.clear();

      // Initialize tenant context
      await initializeTenantContext(user.email, user.firstName);

      toast.success(
        t("oauth.signInSuccess", { provider: session.provider || "WiAccount" }),
      );

      // Show animated loading overlay
      setStatus("success");
    } catch (err) {
      let errorMessage = "OAuth authentication failed. Please try again.";

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      } else if (err && typeof err === "object" && "message" in err) {
        errorMessage = String((err as { message: unknown }).message);
      }

      setError(errorMessage);
      setStatus("error");
      toast.error(errorMessage);
      oauth2Session.clear();

      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 3000);
    }
  }, [searchParams, navigate, t]);

  useEffect(() => {
    processCallback();
  }, [processCallback]);

  const handleLoadingDone = useCallback(() => {
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  // Success — show animated loading overlay
  if (status === "success") {
    return <LoadingOverlay onDone={handleLoadingDone} />;
  }

  // Error state
  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas p-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="bg-panel rounded-xl border border-hairline p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-destructive/10 mb-4">
              <AlertCircle className="w-7 h-7 text-destructive" />
            </div>
            <h2 className="text-lg font-medium text-ink mb-2">
              Authentication Failed
            </h2>
            <p className="text-sm text-muted mb-4">{error}</p>
            <p className="text-xs text-muted-2">Redirecting to login page...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Loading state (initial OAuth processing)
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-4">
      <motion.div
        className="flex flex-col items-center gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
        <p className="text-sm text-muted font-mono">Completing sign in...</p>
      </motion.div>
    </div>
  );
}
